<template>
    <div class="page-container">
        <div class="flex items-center justify-between mb-8">
            <div class="flex items-center gap-4">
                <div class="bg-blue-600 p-3 rounded-2xl shadow-lg">
                    <LucideIcon :size="24" class="text-white">
                        <path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>
                    </LucideIcon>
                </div>
                <div>
                    <h2 class="page-title text-2xl font-bold text-slate-900">{{ $t('household.stockReport.title') }}</h2>
                    <p class="section-subtitle text-slate-500">{{ $t('household.stockReport.subtitle') }}</p>
                </div>
            </div>
        </div>

        <!-- Карта выгрузки -->
        <div class="export-card mb-8">
            <div class="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                <h3 class="font-extrabold text-slate-900 text-base uppercase tracking-tight">{{ $t('household.shared.makeExport') }}</h3>
            </div>
            <div class="p-6">
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div class="export-field">
                        <label class="export-label">{{ $t('household.stockReport.warehouseLabel') }}</label>
                        <select v-model="exportWarehouse" class="export-select">
                            <option value="">{{ $t('household.shared.allWarehouses') }}</option>
                            <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
                        </select>
                    </div>
                    <div class="export-field">
                        <label class="export-label">{{ $t('household.stockReport.nomTypeLabel') }}</label>
                        <select v-model="exportType" class="export-select">
                            <option value="">{{ $t('household.stockReport.typeAll') }}</option>
                            <option value="fuel">{{ $t('household.stockReport.typeFuel') }}</option>
                            <option value="dogs">{{ $t('household.stockReport.typeDogs') }}</option>
                            <option value="goods">{{ $t('household.stockReport.typeGoods') }}</option>
                            <option value="weapons">{{ $t('household.stockReport.typeWeapons') }}</option>
                        </select>
                    </div>
                    <div class="export-field">
                        <label class="export-label">{{ $t('household.shared.dateFrom') }}</label>
                        <DatePicker v-model="exportDateFrom" format="iso" class="export-input" />
                    </div>
                    <div class="export-field">
                        <label class="export-label">{{ $t('household.shared.dateTo') }}</label>
                        <DatePicker v-model="exportDateTo" format="iso" class="export-input" />
                    </div>
                </div>
                <button
                    @click="handleExport"
                    class="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-all shadow-md active:scale-95"
                >
                    <LucideIcon :size="18"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></LucideIcon>
                    {{ $t('household.shared.exportBtn') }}
                </button>
            </div>
        </div>

        <!-- Итоги -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div class="stat-card">
                <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.stockReport.totalPositions') }}</p>
                <p class="text-4xl font-extrabold text-slate-900">{{ filteredData.length }}</p>
            </div>
            <div class="stat-card">
                <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.stockReport.totalItems') }}</p>
                <p class="text-4xl font-extrabold text-slate-900">{{ totalItems.toLocaleString() }}</p>
            </div>
            <div class="stat-card">
                <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.stockReport.totalCost') }}</p>
                <p class="text-4xl font-extrabold text-slate-900">{{ totalValue.toLocaleString() }} ₸</p>
            </div>
        </div>

        <!-- Фильтры -->
        <div class="filter-card mb-8">
            <div class="flex items-center gap-2 mb-4">
                <LucideIcon :size="18" class="text-slate-400"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></LucideIcon>
                <span class="text-sm font-bold text-slate-500 uppercase tracking-wider">{{ $t('household.shared.filters') }}</span>
            </div>
            <div class="flex flex-wrap gap-4">
                <div class="relative flex-1 min-w-[300px] max-w-[400px]">
                    <LucideIcon :size="18" class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
                    </LucideIcon>
                    <input
                        type="text"
                        :placeholder="$t('household.stockReport.searchPlaceholder')"
                        v-model="searchTerm"
                        class="search-input w-full pl-10 pr-4 py-3"
                    />
                </div>
                <select v-model="filterWarehouse" class="type-select min-w-[200px] px-4">
                    <option value="all">{{ $t('household.shared.allWarehouses') }}</option>
                    <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
                </select>
                <select v-model="filterType" class="type-select min-w-[200px] px-4">
                    <option value="all">{{ $t('household.stockReport.typeAll') }}</option>
                    <option value="fuel">{{ $t('household.stockReport.typeFuel') }}</option>
                    <option value="dogs">{{ $t('household.stockReport.typeDogs') }}</option>
                    <option value="goods">{{ $t('household.stockReport.typeGoods') }}</option>
                    <option value="weapons">{{ $t('household.stockReport.typeWeapons') }}</option>
                </select>
            </div>
        </div>

        <!-- Таблица -->
        <div class="table-container">
            <div v-if="loading" class="flex justify-center py-20">
                <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            </div>
            <div v-else class="overflow-x-auto">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th class="w-1/3">{{ $t('household.stockReport.colName') }}</th>
                            <th>{{ $t('household.stockReport.colUnit') }}</th>
                            <th class="text-right">{{ $t('household.stockReport.colQuantity') }}</th>
                            <th class="text-right">{{ $t('household.stockReport.colCost') }}</th>
                            <th class="text-center">{{ $t('household.stockReport.colWarehouses') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in filteredData" :key="item.nomenclatureId">
                            <td>
                                <div class="font-bold text-slate-900 text-base mb-0.5">{{ item.nomenclature?.name }}</div>
                                <div class="text-xs text-slate-400 font-medium">{{ item.nomenclature?.description }}</div>
                            </td>
                            <td class="text-slate-500 font-medium">
                                {{ item.nomenclature?.unit }}
                            </td>
                            <td class="text-right font-extrabold text-slate-900">
                                {{ item.totalQuantity.toLocaleString() }}
                            </td>
                            <td class="text-right font-extrabold text-slate-900">
                                {{ item.totalCost.toLocaleString() }} ₸
                            </td>
                            <td class="text-center">
                                <div class="flex flex-wrap justify-center gap-1.5">
                                    <span
                                        v-for="(name, idx) in item.warehouseNames"
                                        :key="idx"
                                        class="px-3 py-1 bg-blue-50 text-blue-600 text-[10px] font-extrabold uppercase rounded-lg border border-blue-100"
                                    >
                                        {{ name }}
                                    </span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div v-if="filteredData.length === 0" class="text-center py-20 text-slate-500 font-medium">
                    {{ $t('household.shared.noData') }}
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.page-container { padding: 2rem; background: #f3f3f5; min-height: 100%; }
.page-title { color: #000000; font-size: 1.75rem; font-weight: 700; margin: 0; }
.section-subtitle { color: #64748b; font-size: 0.875rem; margin-top: 0.25rem; }

.stat-card {
    background: white;
    padding: 1.5rem 2rem;
    border-radius: 1.25rem;
    border: 1px solid #eef0f4;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01);
}

.filter-card {
    background: white;
    padding: 1.75rem;
    border-radius: 1.25rem;
    border: 1px solid #eef0f4;
    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
}

.search-input, .type-select {
    background: #ffffff;
    border: 1px solid #eef0f4;
    border-radius: 1rem;
    color: #030229;
    font-size: 0.875rem;
    font-weight: 600;
    outline: none;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
.search-input:focus, .type-select:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.08);
}

.table-container {
    background: white;
    border-radius: 1.25rem;
    border: 1px solid #e2e8f0;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.data-table { width: 100%; border-collapse: collapse; }
.data-table th {
    background: #ffffff;
    text-align: left;
    padding: 1.25rem 1.5rem;
    color: #94a3b8;
    font-size: 0.65rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    border-bottom: 1px solid #f1f5f9;
}
.data-table td { padding: 1.5rem; border-top: 1px solid #f8fafc; }
.data-table tr:hover { background: #fbfcfe; }

.export-card {
    background: white;
    border-radius: 1.25rem;
    border: 1px solid #eef0f4;
    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
}
.export-field { display: flex; flex-direction: column; }
.export-label { font-size: 0.7rem; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.4rem; }
.export-select, .export-input {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
    padding: 0.6rem 0.75rem;
    font-size: 0.85rem;
    color: #1e293b;
    outline: none;
    transition: all 0.2s;
}
.export-select:focus, .export-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }
</style>

<script setup>
import { ref, onMounted, computed } from 'vue';
import LucideIcon from '@/components/common/LucideIcon.vue';
import { getStockReport } from '@/api/household_reports';
import { getNomenclatures } from '@/api/household_nomenclatures';
import { getWarehouses } from '@/api/household_warehouses';
import DatePicker from '@/components/widgets/DatePicker.vue';

const loading = ref(true);
const rawStock = ref([]);
const nomenclatures = ref([]);
const warehouses = ref([]);

const searchTerm = ref('');
const filterWarehouse = ref('all');
const filterType = ref('all');

const exportWarehouse = ref('');
const exportType = ref('');
const exportDateFrom = ref('');
const exportDateTo = ref('');

const stockReportData = computed(() => {
    // Group raw stock by nomenclature
    const grouped = rawStock.value.reduce((acc, item) => {
        const key = item.nomenclatureId;
        if (!acc[key]) {
            acc[key] = {
                nomenclatureId: key,
                items: [],
                totalQuantity: 0,
                totalCost: 0,
            };
        }
        acc[key].items.push(item);
        acc[key].totalQuantity += item.quantity;
        acc[key].totalCost += item.cost;
        return acc;
    }, {});

    return Object.values(grouped).map(item => {
        const nomenclature = nomenclatures.value.find(n => n.id === item.nomenclatureId);
        const warehouseNames = [...new Set(item.items.map(i => {
            return warehouses.value.find(w => w.id === i.warehouseId)?.name;
        }).filter(Boolean))];

        return {
            ...item,
            nomenclature,
            warehouseNames
        };
    });
});

const filteredData = computed(() => {
    return stockReportData.value.filter(item => {
        const matchesSearch = item.nomenclature?.name.toLowerCase().includes(searchTerm.value.toLowerCase());
        const matchesWarehouse = filterWarehouse.value === 'all' || 
            item.items.some(i => i.warehouseId === filterWarehouse.value);
        const matchesType = filterType.value === 'all' || item.nomenclature?.type === filterType.value;
        
        return matchesSearch && matchesWarehouse && matchesType;
    });
});

const totalValue = computed(() => filteredData.value.reduce((sum, item) => sum + item.totalCost, 0));
const totalItems = computed(() => filteredData.value.reduce((sum, item) => sum + item.totalQuantity, 0));

const fetchData = async () => {
    loading.value = true;
    try {
        const [stockRes, nomRes, whRes] = await Promise.all([
            getStockReport(),
            getNomenclatures(),
            getWarehouses()
        ]);
        rawStock.value = Array.isArray(stockRes.data) ? stockRes.data : (stockRes.data?.results || []);
        nomenclatures.value = Array.isArray(nomRes.data) ? nomRes.data : (nomRes.data?.results || []);
        warehouses.value = Array.isArray(whRes.data) ? whRes.data : (whRes.data?.results || []);
    } catch (error) {
        console.error('Error fetching stock report data:', error);
    } finally {
        loading.value = false;
    }
};

const handleExport = () => {
    const params = {
        warehouse: exportWarehouse.value || null,
        type: exportType.value || null,
        date_from: exportDateFrom.value || null,
        date_to: exportDateTo.value || null
    };
    console.log('Экспорт остатков с параметрами:', params);
};

onMounted(fetchData);
</script>
